import {map, forEach, get, isEmpty, merge, pick, cloneDeep, keys, difference, isMatch} from 'lodash';

import {Probe, ProbeProcessor, ProcessorDefinition} from '../types';
import {enhanceProcessorDefinitionWithServiceProperties} from './extensibleDataCollector';
import {filterProcessorDefinitionTypes} from './select';
import {getProcessorByStageName, getOutputNameByStageName} from '../stageUtils';

type ProcessorDefinitionEnhancer = (props: {
  probe: Probe;
  processor: ProbeProcessor;
  processorDefinition: ProcessorDefinition;
  processorDefinitionsByName: Record<string, ProcessorDefinition>;
  enhanceProcessorDefinition: (processor: ProbeProcessor) => void;
  [rest: string]: any;
}) => ProcessorDefinition | undefined;

const extensibleDataCollectorProcessorEnhancer: ProcessorDefinitionEnhancer = (
  {processor, processorDefinition, telemetryServiceRegistryItems}
) => {
  if (processorDefinition.name === 'extensible_data_collector') {
    return enhanceProcessorDefinitionWithServiceProperties(
      processorDefinition, processor, telemetryServiceRegistryItems
    );
  }
};

const processorTypeByMetaFilterer: ProcessorDefinitionEnhancer = (
  {processor, processorDefinition}
) => {
  const filteredTypes = {};
  forEach(processorDefinition.outputs, (output, outputName) => {
    forEach(output.types, (outputType) => {
      if (!isEmpty(outputType.meta) && isMatch(processor.properties, outputType.meta)) {
        (filteredTypes[outputName] ??= []).push(outputType);
      }
    });
  });
  if (!isEmpty(filteredTypes)) {
    processorDefinition = cloneDeep(processorDefinition);
    forEach(processorDefinition.outputs, (output, outputName) => {
      output.types = filteredTypes[outputName];
    });
    return processorDefinition;
  }
};

const staticContextKeysExtractor: ProcessorDefinitionEnhancer = (
  {processor, processorDefinition}
) => {
  if (get(processorDefinition.schema, ['additionalProperties', 'type']) === 'string') {
    const schemaProperties = keys(processorDefinition.schema.properties);
    const actualProperties = keys(processor.properties);
    const extraKeys = difference(actualProperties, schemaProperties);
    if (!isEmpty(extraKeys)) {
      processorDefinition = cloneDeep(processorDefinition);
      forEach(processorDefinition.outputs, (output) => {
        forEach(output.types, (outputType) => {
          forEach(extraKeys, (key) => {
            outputType.keys[key] ??= 'any';
          });
        });
      });
      return processorDefinition;
    }
  }
};

const selectProcessorEnhancer: ProcessorDefinitionEnhancer = (
  {probe, processor, processorDefinition, processorDefinitionsByName, enhanceProcessorDefinition}
) => {
  if (processorDefinition.name === 'select' && processor.inputs.in?.stage) {
    const sourceProcessor = getProcessorByStageName({probe, stageName: processor.inputs.in.stage});
    if (sourceProcessor) {
      // source processor definition needs to be enhanced before because select processor
      // takes into account source processor's output values
      enhanceProcessorDefinition(sourceProcessor);
      return filterProcessorDefinitionTypes(processorDefinition, probe, processor, processorDefinitionsByName);
    }
  }
};

const analyticalProcessorsEnhancer: ProcessorDefinitionEnhancer = (
  {probe, processor, processorDefinition, processorDefinitionsByName, enhanceProcessorDefinition}
) => {
  if (!isEmpty(processorDefinition.inputs)) {
    let keysFromSources = {};
    forEach(processor.inputs, (input) => {
      if (input?.stage) {
        const sourceProcessor = getProcessorByStageName({probe, stageName: input.stage});
        if (sourceProcessor) {
          enhanceProcessorDefinition(sourceProcessor);
          if (processorDefinitionsByName[sourceProcessor.name]) {
            const outputName = getOutputNameByStageName({processor: sourceProcessor, stageName: input.stage});
            if (outputName) {
              const stageDefinition = processorDefinitionsByName[sourceProcessor.name].outputs[outputName];
              // NOTE(vkramskikh): should we check for keys with the same name but different type?
              if (stageDefinition) merge(keysFromSources, ...map(stageDefinition.types, 'keys'));
            }
          }
        }
      }
    });
    const propertySchema = processorDefinition.schema.properties;
    if (
      get(propertySchema, ['significant_keys', 'type']) === 'array' &&
      get(propertySchema, ['significant_keys', 'items', 'type']) === 'string' &&
      !isEmpty(processor.properties.significant_keys)
    ) {
      keysFromSources = pick(keysFromSources, processor.properties.significant_keys);
    }
    if (
      get(propertySchema, ['group_by', 'type']) === 'array' &&
      get(propertySchema, ['group_by', 'items', 'type']) === 'string'
    ) {
      keysFromSources = pick(keysFromSources, processor.properties.group_by);
    }
    if (!isEmpty(keysFromSources)) {
      processorDefinition = cloneDeep(processorDefinition);
      forEach(processorDefinition.outputs.out.types, (outputType) => {
        outputType.keys = keysFromSources;
      });
      return processorDefinition;
    }
  }
};

export default [
  extensibleDataCollectorProcessorEnhancer,
  processorTypeByMetaFilterer,
  staticContextKeysExtractor,
  analyticalProcessorsEnhancer,
  selectProcessorEnhancer,
] as ProcessorDefinitionEnhancer[];
